import React from "react";
import { Box, Button, Grid, GridItem, HStack, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import type { RunnerState } from "../flow/engine/useFlowRunner";
import { stepById } from "../flow/flowGraph";
import { formatDuration } from "../flow/estimateTime";
import { copyText } from "./statusMeta";
import useNotification from "../../../utils/useNotification";

interface Props {
  state: RunnerState;
  onViewOrder: (orderNo: string) => void;
  onViewLog: () => void;
  onRetry: () => void;
  onRestart: () => void;
  onNewRun: () => void;
}

const MotionBox = motion(Box);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <GridItem>
    <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.06em" color="gray.500" fontWeight="600">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="700" fontFamily="mono">
      {value}
    </Text>
  </GridItem>
);

const ResultSummary: React.FC<Props> = ({ state, onViewOrder, onViewLog, onRetry, onRestart, onNewRun }) => {
  const toast = useNotification();
  if (state.status !== "completed" && state.status !== "failed" && state.status !== "cancelled") return null;

  const duration = state.startedAt && state.endedAt ? state.endedAt - state.startedAt : 0;
  const runs = Object.values(state.runs);
  const completed = runs.filter((r) => r.status === "completed").length;
  const skipped = runs.filter((r) => r.status === "skipped").length;
  const orderNo = state.snapshot?.child?.orderNo || state.snapshot?.enquiry?.orderNo;
  const target = state.targetStepId ? stepById(state.targetStepId) : undefined;
  const failedStep = state.failedStepId ? stepById(state.failedStepId) : undefined;
  const failedRun = state.failedStepId ? state.runs[state.failedStepId] : undefined;
  const success = state.status === "completed";
  const tone = success ? "green" : state.status === "failed" ? "red" : "gray";

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      borderWidth="1px"
      borderColor={`${tone}.300`}
      bg={`${tone}.50`}
      borderRadius="lg"
      p={4}
    >
      <HStack justify="space-between" align="flex-start" mb={3}>
        <Box>
          <Text fontSize="sm" fontWeight="700" color={`${tone}.700`}>
            {success
              ? state.targetReached
                ? `Target reached: ${target?.targetLabel}`
                : "Branch action completed"
              : state.status === "failed"
              ? `Flow halted at "${failedStep?.name}"`
              : "Execution cancelled"}
          </Text>
          {!success && failedRun?.error && (
            <Text fontSize="xs" color="red.700" mt={1}>
              {failedRun.error.reason}
              {failedRun.error.expected ? ` · expected ${failedRun.error.expected}` : ""}
              {failedRun.error.actual ? ` · actual ${failedRun.error.actual}` : ""}
            </Text>
          )}
        </Box>
        <HStack wrap="wrap" justify="flex-end">
          {orderNo && (
            <>
              <Button size="xs" variant="outline" bg="white" onClick={() => onViewOrder(orderNo)}>
                View order
              </Button>
              <Button
                size="xs"
                variant="outline"
                bg="white"
                onClick={async () => {
                  await copyText(orderNo);
                  toast({ status: "success", description: `${orderNo} copied` });
                }}
              >
                Copy order number
              </Button>
            </>
          )}
          <Button size="xs" variant="outline" bg="white" onClick={onViewLog}>
            View execution log
          </Button>
          {state.status === "failed" && (
            <>
              <Button size="xs" variant="outline" bg="white" onClick={onRestart}>
                Restart flow
              </Button>
              <Button size="xs" colorScheme="red" onClick={onRetry}>
                Retry step
              </Button>
            </>
          )}
          {success && (
            <Button size="xs" colorScheme="green" onClick={onNewRun}>
              Run another test
            </Button>
          )}
        </HStack>
      </HStack>
      <Grid templateColumns="repeat(6, 1fr)" gap={3}>
        <Stat label="Order" value={orderNo || "—"} />
        <Stat label="Target" value={target?.targetLabel || "—"} />
        <Stat label="Total time" value={formatDuration(duration)} />
        <Stat label="Steps" value={`${completed} done${skipped ? ` · ${skipped} skipped` : ""}`} />
        <Stat label="Events" value={state.eventsProcessed} />
        <Stat label="Polling checks" value={state.totalPollChecks} />
      </Grid>
    </MotionBox>
  );
};

export default ResultSummary;
